<template>
	<div>
		<v-alert v-if="!report.period.ossActive" dense text type="info" class="mb-2">
			Senza adesione all’OSS nel periodo le vendite B2C UE si fatturano con l’IVA italiana: il quadro mostra quanto si dichiarerebbe con l’OSS e la differenza.
		</v-alert>
		<SectionCard title="Quadro OSS" icon="mdi-earth" :subtitle="subtitle" no-padding data-tour="oss">
			<template #actions>
				<v-btn v-if="report.period.quarter" small text color="primary" @click="$emit('print')"><v-icon small left>mdi-printer-outline</v-icon>Stampa</v-btn>
			</template>
			<v-simple-table dense>
				<thead>
					<tr>
						<th>Paese</th>
						<th>Aliquota</th>
						<th class="text-right">Imponibile</th>
						<th class="text-right">IVA dovuta</th>
						<th class="text-right">IVA italiana applicata</th>
						<th class="text-right">Differenza</th>
						<th class="text-right">Ordini</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="r in report.oss" :key="`${r.country}:${r.rate}`">
						<td>
							<span class="fo-code">{{ r.country }}</span> {{ r.name }}
						</td>
						<td class="text-no-wrap">
							{{ formatPct(r.rate) }} <span class="fo-muted">{{ r.rateType === 'STANDARD' ? 'ordinaria' : 'ridotta' }}</span>
							<v-icon v-if="r.ecbAdjusted" x-small color="primary" title="Convertito con il cambio BCE dell’ultimo giorno del periodo">mdi-swap-horizontal</v-icon>
						</td>
						<td class="text-right">{{ formatEurCents(r.net) }}</td>
						<td class="text-right fo-strong">{{ formatEurCents(r.vat) }}</td>
						<td class="text-right">{{ formatEurCents(r.itVat) }}</td>
						<td class="text-right" :class="r.diff > 0 ? 'red--text text--darken-1' : 'green--text text--darken-1'">{{ signed(r.diff) }}</td>
						<td class="text-right">{{ formatNumber(r.orders) }}</td>
					</tr>
					<tr v-if="!report.oss.length">
						<td colspan="7" class="text-center grey--text py-4">Nessuna vendita B2C verso altri Paesi UE nel periodo.</td>
					</tr>
				</tbody>
				<tfoot v-if="report.oss.length">
					<tr>
						<td colspan="2" class="fo-strong">Totale</td>
						<td class="text-right fo-strong">{{ formatEurCents(report.summary.oss.net) }}</td>
						<td class="text-right fo-strong">{{ formatEurCents(report.summary.oss.vat) }}</td>
						<td class="text-right fo-strong">{{ formatEurCents(report.summary.oss.itVat) }}</td>
						<td class="text-right fo-strong">{{ signed(report.summary.oss.diff) }}</td>
						<td></td>
					</tr>
				</tfoot>
			</v-simple-table>
			<div class="fo-muted pa-3">
				Stesso lordo pagato dal cliente, IVA scorporata con l’aliquota del Paese. Una differenza positiva è IVA in più rispetto a quella italiana: a prezzi invariati riduce il margine.
				<span v-if="report.period.ecbDate">Ordini in valuta convertiti con il cambio BCE del {{ report.period.ecbDate | moment('DD/MM/YYYY') }}.</span>
			</div>
		</SectionCard>

		<SectionCard title="Rettifiche di periodi precedenti" icon="mdi-undo-variant" subtitle="rimborsi di ordini di trimestri già chiusi" no-padding class="mt-2" data-tour="corrections">
			<v-simple-table dense>
				<thead>
					<tr>
						<th>Paese</th>
						<th>Trimestre d’origine</th>
						<th>Aliquota</th>
						<th class="text-right">Imponibile</th>
						<th class="text-right">IVA</th>
						<th class="text-right">Rimborsi</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="c in report.corrections" :key="`${c.country}:${c.period}:${c.rate}`">
						<td>
							<span class="fo-code">{{ c.country }}</span> {{ countryName(c.country) }}
						</td>
						<td>{{ c.period.replace('-Q', ' T') }}</td>
						<td>{{ formatPct(c.rate) }}</td>
						<td class="text-right">{{ formatEurCents(c.net) }}</td>
						<td class="text-right">{{ formatEurCents(c.vat) }}</td>
						<td class="text-right">{{ formatNumber(c.orders) }}</td>
					</tr>
					<tr v-if="!report.corrections.length">
						<td colspan="6" class="text-center grey--text py-4">Nessuna rettifica: i rimborsi del periodo riguardano ordini dello stesso trimestre.</td>
					</tr>
				</tbody>
			</v-simple-table>
		</SectionCard>
	</div>
</template>

<script>
import SectionCard from '@/components/core/SectionCard.vue';
import { formatEurCents, formatNumber, formatPct } from '@/components/finance/format';
import { countryName } from './labels';

export default {
	name: 'ForeignOss',
	components: { SectionCard },
	props: {
		report: { type: Object, required: true },
	},
	computed: {
		subtitle() {
			return this.report.period.ossActive ? 'da dichiarare per Paese e aliquota' : 'simulazione';
		},
	},
	methods: {
		formatEurCents,
		formatNumber,
		formatPct,
		countryName,
		signed: v => `${v > 0 ? '+' : ''}${formatEurCents(v)}`,
	},
};
</script>

<style scoped>
.fo-code {
	font-family: monospace;
	font-size: 12px;
	color: #5f6b7a;
}
.fo-strong {
	font-weight: 600;
}
.fo-muted {
	font-size: 12px;
	color: #5f6b7a;
}
</style>
